<template v-if="cms.data?.value?.content">
  <div v-for="content in cms.data?.value?.content.content" :key="content._uid">
    <component :is="content.component" :blok="content" />
  </div>
  <Skeleton
    v-for="i in 4"
    v-show="fetchingCategories === 'pending'"
    :key="i"
    type="custom"
    class="mb-2 h-6 w-44"
  />
  <ul v-show="fetchingCategories !== 'pending'">
    <li
      v-for="category in categories"
      :key="category.url as string"
      class="mb-2"
    >
      <AgsLink
        :to="category.url"
        hover="underline"
        size="lg"
        :in-paragraph="true"
      >
        {{ category.name }}
      </AgsLink>
    </li>
  </ul>
  <div
    v-for="postContent in cms.data?.value?.content.post_content"
    :key="postContent._uid"
  >
    <component :is="postContent.component" :blok="postContent" />
  </div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { SbEmptySearchPage } from '~/cms/storyblok/types/storyblok.d.ts'

type SuggestedCategory = {
  name: string
  url: RouteLocationRaw
}

const { runWithContext } = useNuxtApp()
const { toLocalePath } = useRouteHelpers()

const cms = useCms<SbEmptySearchPage>('search-page')

const fetchCategories = async (
  cmsCategories: { slug: string }[] = [],
): Promise<SuggestedCategory[]> => {
  const categoryIds = cmsCategories.map(({ slug }) => parseInt(slug))

  const { data, refresh } = await runWithContext(() =>
    useListCategories({
      key: `useListCategories - ${categoryIds.join(',')}`,
      params: { categoryIds },
      options: { immediate: false },
    }),
  )

  await refresh()

  if (!data.value) return []

  return data.value.reduce((acc, { name, path }) => {
    if (!name) return acc
    acc.push({
      name,
      url: toLocalePath(path),
    })
    return acc
  }, [] as SuggestedCategory[])
}

const { data: categories, status: fetchingCategories } = await useAsyncData<
  SuggestedCategory[]
>(
  'suggested-categories',
  async () => {
    await cms.fetchBySlug('empty-search', {
      resolveRelations: 'EmptySearchPage.suggested_categories',
    })

    return fetchCategories(cms.data?.value?.content?.suggested_categories)
  },
  { default: () => [] },
)
</script>
